import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useMutation } from '@apollo/client';

import { ICreatePaymentIntentForExistingPMQueryResult } from '@monofront/types';
import { CREATE_PAYMENT_INTENT_FOR_EXISTING_PM } from '@monofront/business/queries';
import {
  useCheckoutCartState,
  useCheckoutComMachine,
} from '@monofront/business/contexts';
import {
  CHECKOUTCOM_CARD_FORM_TRANSITIONS,
  CHECKOUT_COM_CARD_FORM_STATES,
} from '@monofront/business/stateMachines';
import Button from '@monofront/design-system/components/Button';

import { useAuthentication } from '@context/businessContainer';
import logger from '@utils/logger';
import useCompleteOrder from '../hooks/useCompleteOrder';
import { useCheckoutPayment } from '../contexts';
import useHandlePaymentError from '../hooks/useHandlePaymentErrors';

/**
 * Display a Pay CTA to pay with an existing checkoutCom card
 */
const PayWithExistingCardCta: React.FC = () => {
  const tCheckout = useTranslations('Checkout');
  const { checkoutCart } = useCheckoutCartState();
  const { authUser } = useAuthentication();
  const { state, send, registerCompleteOrder } = useCheckoutComMachine();
  const {
    isOrderValidationInProgress,
    setIsOrderValidationInProgress,
    selectedExistingPaymentMethod,
  } = useCheckoutPayment();
  const { completeOrder } = useCompleteOrder();
  const { handlePaymentError } = useHandlePaymentError();

  useEffect(() => {
    registerCompleteOrder(() => completeOrder());
  }, [registerCompleteOrder, completeOrder]);

  const [createPaymentIntentForExistingPM] =
    useMutation<ICreatePaymentIntentForExistingPMQueryResult>(
      CREATE_PAYMENT_INTENT_FOR_EXISTING_PM,
    );

  const validateOrder = async () => {
    setIsOrderValidationInProgress(true);

    send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.SUBMIT_CARD });
    try {
      if (
        !checkoutCart?.id ||
        !checkoutCart?.payment?.id ||
        !selectedExistingPaymentMethod
      ) {
        throw new Error(
          'Try to validate an order with incomplete data (pay with an existing Card).',
        );
      }

      const { data } = await createPaymentIntentForExistingPM({
        variables: {
          tokenValue: checkoutCart.id,
          cartPaymentId: checkoutCart.payment.id,
          paymentMethodId:
            selectedExistingPaymentMethod.existingPaymentMethodId,
          paymentMethodCode: selectedExistingPaymentMethod.paymentMethodCode,
        },
      });

      if (!data?.createPaymentIntentForExistingPM?.payment) {
        throw new Error('Create Payment Intent failed with empty response.');
      }

      const url3DSecure =
        data.createPaymentIntentForExistingPM.payment
          .financialPaymentRedirectLink;

      if (url3DSecure) {
        send({
          type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.CREATE_PAYMENT_WITH_3DS,
          url3DSecure,
          paymentId:
            data.createPaymentIntentForExistingPM.payment
              .financialPaymentIntentId,
        });
      } else {
        // LF-3153 - keep a log to see if we really do have non-3DS payments and how frequently - will be remove after a few days.
        const warnMessage = {
          message:
            '[checkout] A non 3DS payment has been validated (existing card).',
          context: {
            user_id: authUser?.uid,
            cart_id: checkoutCart.id,
            cart_payment_id: checkoutCart.payment.id,
            checkout_cart: checkoutCart,
            selected_pm: selectedExistingPaymentMethod,
          },
        };

        logger.warn(warnMessage.message, warnMessage.context);

        const hasCompletedOrder = await completeOrder();

        if (!hasCompletedOrder) {
          send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });
        }
      }
    } catch (error: any) {
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.RESET });
      setIsOrderValidationInProgress(false);

      handlePaymentError({
        code: 'PAY_WITH_EXISTING_CARD_FAILED',
        message:
          !error?.graphQLErrors && error?.message
            ? error.message
            : 'Cannot create a PaymentIntent for an existing card or complete an order for a non-3DS card.',
        shouldDisplayError: true,
        error,
        context: {
          selected_pm: selectedExistingPaymentMethod,
        },
      });
    }
  };

  useEffect(() => {
    // handle checkoutcomCardFormMachine transitions
    if (state.value === CHECKOUT_COM_CARD_FORM_STATES['INITIAL']) {
      setIsOrderValidationInProgress(false);

      return;
    }

    if (state.value === CHECKOUT_COM_CARD_FORM_STATES['3D_SECURE_SUCCEEDED']) {
      // Only ask the machine to submit: it invokes the registered routine once
      // and ignores this event in every later state, so a re-run of this effect
      // cannot complete the order twice.
      send({ type: CHECKOUTCOM_CARD_FORM_TRANSITIONS.COMPLETE_ORDER });
    }
  }, [state]);

  if (!checkoutCart?.id) return null;

  return (
    <Button
      onClick={() => validateOrder()}
      isLoading={isOrderValidationInProgress}
      dataTestId="ActionButtons-order-payment"
    >
      {tCheckout('finishCheckout')}
    </Button>
  );
};

export default PayWithExistingCardCta;
